/**
 * GradeRecalculator -- Client-side mirror of backend grade_recalculator.py
 *
 * DV:P3.B -- Grade Recalculation Utility
 * MDM-DV-P3-001 v3.2.1
 *
 * This is a defect-severity calculation utility, NOT a user behavioral tracker.
 * No user tracking data is collected or rendered.
 *
 * Stateless function: recalculate(annotations, annotationStates)
 * -> { grade, label, confidence, impactBreakdown, originalGrade, gradeDelta }
 */

// CGC 0.5-increment grade scale (matches backend exactly)
const CGC_GRADE_SCALE = [
  0.5, 1.0, 1.5, 1.8, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5,
  5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0, 9.2,
  9.4, 9.6, 9.8, 9.9, 10.0,
];

const CGC_LABELS = {
  10.0: 'Gem Mint',
  9.9: 'Mint',
  9.8: 'Near Mint/Mint',
  9.6: 'Near Mint+',
  9.4: 'Near Mint',
  9.2: 'Near Mint-',
  9.0: 'Very Fine/Near Mint',
  8.5: 'Very Fine+',
  8.0: 'Very Fine',
  7.5: 'Very Fine-',
  7.0: 'Fine/Very Fine',
  6.5: 'Fine+',
  6.0: 'Fine',
  5.5: 'Fine-',
  5.0: 'Very Good/Fine',
  4.5: 'Very Good+',
  4.0: 'Very Good',
  3.5: 'Very Good-',
  3.0: 'Good/Very Good',
  2.5: 'Good+',
  2.0: 'Good',
  1.8: 'Good-',
  1.5: 'Fair/Good',
  1.0: 'Fair',
  0.5: 'Poor',
};

function snapToCgcScale(rawGrade) {
  if (rawGrade >= 10.0) return 10.0;
  if (rawGrade <= 0.5) return 0.5;
  let closest = CGC_GRADE_SCALE[0];
  let minDiff = Math.abs(CGC_GRADE_SCALE[0] - rawGrade);
  for (let i = 1; i < CGC_GRADE_SCALE.length; i++) {
    const diff = Math.abs(CGC_GRADE_SCALE[i] - rawGrade);
    if (diff < minDiff) {
      minDiff = diff;
      closest = CGC_GRADE_SCALE[i];
    }
  }
  return closest;
}

export function recalculate(annotations = [], annotationStates = {}) {
  const impactBreakdown = [];
  const categoryImpacts = {};
  let originalTotalImpact = 0;
  let adjustedTotalImpact = 0;
  let confirmedCount = 0;
  let dismissedCount = 0;
  const totalCount = annotations.length;

  for (const ann of annotations) {
    const state = annotationStates[ann.id];
    const feedback = state?.status || ann.feedback || 'unreviewed';
    const gradeImpact = ann.grade_impact || 0.5;

    originalTotalImpact += gradeImpact;

    let included = true;
    let effectiveImpact = gradeImpact;

    if (feedback === 'dismissed') {
      included = false;
      dismissedCount++;
    } else if (feedback === 'confirmed') {
      confirmedCount++;
    } else if (feedback === 'adjusted' && state?.adjustedSeverity) {
      confirmedCount++;
      // Approximate adjusted impact based on severity ratio
      const severityMultipliers = { minor: 0.5, moderate: 1.0, heavy: 1.5 };
      const origMult = severityMultipliers[ann.severity] || 1.0;
      const newMult = severityMultipliers[state.adjustedSeverity] || 1.0;
      effectiveImpact = gradeImpact * (newMult / origMult);
    }

    if (included) {
      adjustedTotalImpact += effectiveImpact;
    }

    // Track by category for stacked bar
    const cat = ann.cgc_category || 'UNKNOWN';
    if (included) {
      categoryImpacts[cat] = (categoryImpacts[cat] || 0) + effectiveImpact;
    }

    impactBreakdown.push({
      annotationId: ann.id,
      defectName: ann.defect_id || ann.cgc_category || 'Unknown',
      severity: state?.adjustedSeverity || ann.severity,
      gradeImpact: Math.round((included ? effectiveImpact : gradeImpact) * 100) / 100,
      included,
      status: feedback,
    });
  }

  const originalGrade = snapToCgcScale(10.0 - originalTotalImpact);
  const adjustedGrade = snapToCgcScale(10.0 - adjustedTotalImpact);
  const gradeDelta = Math.round((adjustedGrade - originalGrade) * 10) / 10;

  let confidence;
  if (totalCount === 0) {
    confidence = 100;
  } else {
    const reviewedCount = confirmedCount + dismissedCount;
    const reviewRatio = reviewedCount / totalCount;
    confidence = Math.min(100, Math.floor(50 + reviewRatio * 50));
  }

  return {
    grade: adjustedGrade,
    /* c8 ignore next */
    label: CGC_LABELS[adjustedGrade] || `Grade ${adjustedGrade}`,
    confidence,
    impactBreakdown,
    originalGrade,
    gradeDelta,
    totalImpact: Math.round(adjustedTotalImpact * 100) / 100,
    categoryImpacts,
  };
}

export { CGC_GRADE_SCALE, CGC_LABELS, snapToCgcScale };
export default { recalculate, CGC_GRADE_SCALE, CGC_LABELS, snapToCgcScale };
